import { X, Share2, Copy, Check, FileDown, Trash2, Lightbulb, Target, TrendingUp } from "lucide-react";
import { useState } from "react";

/**
 * Full-detail view of a saved proposal with Share + Copy + Export options.
 * Reuses the same payload shape persisted by the auto-save flow.
 */
export default function ProposalDetail({ proposal, onClose, onDelete, lang = "ar" }) {
  const [copied, setCopied] = useState(false);
  if (!proposal) return null;

  const r = proposal.result || {};
  const inp = proposal.inputs || {};
  const currency = inp.currency || "SAR";
  const curSym = lang === "ar" ? "ر.س" : "SAR";
  const date = new Date(proposal.created_at).toLocaleString("ar-SA");

  // Plain-text share template — WhatsApp / SMS / email all accept it.
  const buildShareText = () => {
    const lines = [];
    lines.push(`💰 عرض سعر من حاسبة برولانسر`);
    lines.push(`─────────────`);
    lines.push(`الخدمة: ${proposal.service || "—"}`);
    if (inp.projectDescription) lines.push(`الوصف: ${inp.projectDescription}`);
    lines.push(`الكمية: ${inp.quantity || 1} ${inp.unit || ""}`);
    if (r.recommended_price != null) lines.push(`السعر الموصى: ${r.recommended_price} ${curSym}`);
    if (r.win_rate_price != null) lines.push(`سعر الفوز بالطلب: ${r.win_rate_price} ${curSym}`);
    lines.push(`─────────────`);
    lines.push(`🔗 احسب سعرك: https://prolancer.app`);
    return lines.join("\n");
  };

  const share = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try { await navigator.share({ title: proposal.title, text }); return; } catch {}
    }
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  };

  return (
    <div
      dir="rtl"
      data-testid="proposal-detail"
      className="fixed inset-0 z-[160] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-[#182138]/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl bg-[#1f2a47] border border-[#2e3a5c] max-h-[92vh] overflow-y-auto font-arabic"
      >
        <div className="sticky top-0 bg-[#1f2a47] border-b border-[#2e3a5c] px-5 py-3 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0">
            <h3 className="font-extrabold text-white truncate">{proposal.title}</h3>
            <p className="text-[11px] text-white/50 mt-0.5">{date}</p>
          </div>
          <button
            data-testid="proposal-detail-close"
            onClick={onClose}
            className="p-2 rounded-full text-white/60 hover:text-white hover:bg-white/5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Headline prices */}
          <div className="grid grid-cols-2 gap-3">
            {r.recommended_price != null && (
              <div className="rounded-2xl bg-[#18c98f]/10 border border-[#18c98f]/40 p-4">
                <div className="flex items-center gap-1.5 text-[11px] text-[#18c98f]">
                  <Target className="h-3.5 w-3.5" /> السعر الموصى
                </div>
                <div className="mt-1 font-mono-num text-2xl font-extrabold text-[#18c98f]">
                  {r.recommended_price} <span className="text-sm">{curSym}</span>
                </div>
              </div>
            )}
            {r.win_rate_price != null && (
              <div className="rounded-2xl bg-[#182138] border border-[#2e3a5c] p-4">
                <div className="flex items-center gap-1.5 text-[11px] text-amber-400">
                  <TrendingUp className="h-3.5 w-3.5" /> سعر الفوز
                </div>
                <div className="mt-1 font-mono-num text-2xl font-extrabold text-amber-400">
                  {r.win_rate_price} <span className="text-sm">{curSym}</span>
                </div>
              </div>
            )}
          </div>

          {/* Price options */}
          {Array.isArray(r.price_options) && r.price_options.length > 0 && (
            <div>
              <h4 className="text-xs font-extrabold text-white/80 mb-2">خيارات التسعير</h4>
              <ul className="space-y-2">
                {r.price_options.map((opt, i) => (
                  <li key={i} className="rounded-xl bg-[#182138] border border-[#2e3a5c] p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-white/80">{opt.label}</span>
                      <span className="font-mono-num text-base font-extrabold text-[#18c98f]">
                        {opt.price} <span className="text-[10px]">{curSym}</span>
                      </span>
                    </div>
                    {opt.note && <p className="mt-1 text-[11px] text-white/50 leading-relaxed">{opt.note}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Inputs recap */}
          <div className="rounded-xl bg-[#182138] border border-[#2e3a5c] p-3 text-xs text-white/70 space-y-1">
            {proposal.service && <div>الخدمة: <b className="text-white">{proposal.service}</b></div>}
            {inp.experience && <div>الخبرة: <b className="text-white">{inp.experience}</b></div>}
            {inp.complexity && <div>التعقيد: <b className="text-white">{inp.complexity}</b></div>}
            {inp.quantity && <div>الكمية: <b className="text-white">{inp.quantity} {inp.unit || ""}</b></div>}
            {inp.projectDescription && <div className="pt-1 border-t border-[#2e3a5c]/60">الوصف: <span className="text-white/90">{inp.projectDescription}</span></div>}
          </div>

          {/* AI Reasoning */}
          {r.reasoning && (
            <div className="rounded-xl bg-[#182138] border border-[#2e3a5c] p-3">
              <h4 className="text-xs font-extrabold text-white/80 mb-1.5 flex items-center gap-1">
                <Lightbulb className="h-3.5 w-3.5 text-amber-400" />
                تحليل الذكاء الاصطناعي
              </h4>
              <p className="text-[12px] text-white/75 leading-relaxed">{r.reasoning}</p>
            </div>
          )}

          {/* Tips */}
          {Array.isArray(r.tips) && r.tips.length > 0 && (
            <div>
              <h4 className="text-xs font-extrabold text-white/80 mb-2">نصائح للزيادة</h4>
              <ul className="space-y-1.5">
                {r.tips.map((tip, i) => (
                  <li key={i} className="text-[12px] text-white/75 leading-relaxed flex gap-2">
                    <span className="text-[#18c98f] shrink-0">◆</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 bg-[#1f2a47] border-t border-[#2e3a5c] p-3 flex items-center gap-2">
          <button
            data-testid="proposal-share"
            onClick={share}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-[#18c98f] text-white text-sm font-extrabold hover:brightness-110"
          >
            <Share2 className="h-4 w-4" />
            مشاركة
          </button>
          <button
            data-testid="proposal-copy"
            onClick={copy}
            className="shrink-0 inline-flex items-center justify-center gap-1.5 h-11 px-4 rounded-full bg-[#182138] border border-[#2e3a5c] text-white text-sm font-bold hover:border-[#18c98f]"
          >
            {copied ? <Check className="h-4 w-4 text-[#18c98f]" /> : <Copy className="h-4 w-4" />}
            {copied ? "تم" : "نسخ"}
          </button>
          <button
            data-testid="proposal-detail-delete"
            onClick={() => { if (window.confirm("حذف هذا العرض؟")) onDelete?.(proposal.id); }}
            className="shrink-0 p-2.5 rounded-full bg-[#182138] border border-[#2e3a5c] text-red-400 hover:border-red-500"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
